/**
 * MainLayout - Layout principal avec sidebar et topbar
 * Inspiré du projet RT20
 */

import { useState, useEffect, ReactNode } from 'react';
import Sidebar, { MenuId } from './Sidebar';
import TopBar from './TopBar';
import TopBarUserActions from './TopBarUserActions';
import { SavUser } from '../../SupabaseWrapper';
import { useViewport } from '../../hooks/useViewport';

interface MainLayoutProps {
  children: ReactNode;
  activeMenu: MenuId;
  setActiveMenu: (menu: MenuId) => void;
  pageTitle: string;
  pageSubtitle?: string;
  user: SavUser | null;
  onLogout: () => void;
}

function MainLayout({
  children,
  activeMenu,
  setActiveMenu,
  pageTitle,
  pageSubtitle,
  user,
  onLogout
}: MainLayoutProps) {
  // État de la sidebar (collapsée ou non) - persisté dans localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sav_sidebarCollapsed');
    console.log('[MainLayout] Initial sidebarCollapsed from localStorage:', saved);
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Sauvegarder l'état de la sidebar dans localStorage
  useEffect(() => {
    console.log('[MainLayout] Saving sidebarCollapsed to localStorage:', sidebarCollapsed);
    localStorage.setItem('sav_sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleToggleSidebar = () => {
    console.log('[MainLayout] Toggle sidebar - current:', sidebarCollapsed);
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const { isDesktop } = useViewport();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fermer automatiquement le drawer si l'utilisateur passe en desktop pendant qu'il est ouvert
  useEffect(() => {
    if (isDesktop && drawerOpen) {
      setDrawerOpen(false);
    }
  }, [isDesktop, drawerOpen]);

  // Lock le scroll du body quand le drawer est ouvert (mobile/tablet)
  useEffect(() => {
    if (drawerOpen) {
      document.body.classList.add('servup-drawer-open');
    } else {
      document.body.classList.remove('servup-drawer-open');
    }
    return () => {
      document.body.classList.remove('servup-drawer-open');
    };
  }, [drawerOpen]);

  // Escape ferme le drawer
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [drawerOpen]);

  // Wrapper et closer pour les items de menu : ferme le drawer après navigation sur mobile
  const handleSetActiveMenu = (menu: MenuId) => {
    setActiveMenu(menu);
    if (!isDesktop) setDrawerOpen(false);
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: 'calc(100vh - var(--bandeau-env))',
      backgroundColor: '#f1f5f9',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Overlay sombre - uniquement en mode drawer ouvert */}
      {!isDesktop && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1100,
          }}
        />
      )}

      {/* Sidebar : sur desktop, position fixe normale.
          Sur mobile/tablet, transformée via translateX selon drawerOpen */}
      {isDesktop ? (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={handleToggleSidebar}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          userPermissions={user?.permissions}
        />
      ) : (
        <div
          style={{
            position: 'fixed',
            top: 'var(--bandeau-env)',
            left: 0,
            width: 260,
            height: 'calc(100vh - var(--bandeau-env))',
            zIndex: 1200,
            transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 250ms ease',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Sidebar
              collapsed={false}
              onToggle={() => setDrawerOpen(false)}
              activeMenu={activeMenu}
              setActiveMenu={handleSetActiveMenu}
              userPermissions={user?.permissions}
              mobileDrawer
            />
          </div>
          <TopBarUserActions
            user={user}
            onLogout={onLogout}
            variant="drawer"
            onAction={() => setDrawerOpen(false)}
          />
        </div>
      )}

      {/* Contenu principal */}
      <div style={{
        flex: 1,
        marginLeft: isDesktop ? (sidebarCollapsed ? '70px' : '260px') : 0,
        transition: 'margin-left 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - var(--bandeau-env))',
        minWidth: 0
      }}>
        {/* TopBar */}
        <TopBar
          pageTitle={pageTitle}
          pageSubtitle={pageSubtitle}
          user={user}
          onLogout={onLogout}
          onOpenDrawer={!isDesktop ? () => setDrawerOpen(true) : undefined}
        />

        {/* Contenu de la page */}
        <main style={{
          flex: 1,
          padding: '24px',
          overflowY: 'auto'
        }}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default MainLayout;
